/**
 * 渠道变体折扣码的派生规则（PRD #17 ③）。
 *
 * campaign 主码派生出每渠道一个变体（`WINTER15` → `WINTER15-IG`），挂在**同一个**
 * Shopify discount 上（同折扣力度/范围/日期），写进该渠道的文案；核销即算该渠道
 * 带来的销售。这是 Instagram **唯一**的归因手段 —— IG 文案带不了外链，GA4 永远
 * 看不到那次访问。
 *
 * ⚠️ 后缀表与后端 `yepairag/core/marketing/variant_codes.py` 的 `_CHANNEL_SUFFIX`
 * 必须一致 —— 这里算出来的码是给商家**照着念/照着输**的，和 Shopify 里真实存在的
 * 码差一个字符就等于给了一个用不了的码。改一边记得改另一边。
 *
 * 前端自己算而不是等后端下发，是因为澄清表单要在**折扣码还没提交**时就预告变体码
 * （PRD 要求四处体现里的第一处），那时后端还没有任何东西可返回。
 */

/** 渠道 → 后缀。短且全大写:顾客要在照片下面手打这个码,`-INSTAGRAM` 招打错。 */
const CHANNEL_SUFFIX: Record<string, string> = {
  instagram: 'IG',
  facebook: 'FB',
  x: 'X',
  blog: 'BLOG',
  shopify_blog: 'BLOG',
  shopify: 'BLOG',
};

/** Shopify 码接受的字符比这多,但这个码是人手打的。与后端 `_CODE_SAFE` 同源。 */
const CODE_SAFE = /[^A-Z0-9-]+/g;

/**
 * `WINTER15` + instagram → `WINTER15-IG`。
 *
 * 返回 null 表示「这个渠道我们不派生变体码」——不是错误,调用方就是没有变体码可显示
 * （和后端 `variant_code()` 的语义一致）。
 */
export function variantCode(mainCode: string | null | undefined, channel: string | null | undefined): string | null {
  const suffix = CHANNEL_SUFFIX[(channel ?? '').trim().toLowerCase()];
  const raw = (mainCode ?? '').trim().toUpperCase().replace(CODE_SAFE, '').replace(/^-+|-+$/g, '');
  const base = stripChannelSuffix(raw);
  if (!suffix || !base) return null;
  return `${base}-${suffix}`;
}

/** 已知后缀,长的先试(`-BLOG` 要在假想的 `-B` 之前)。 */
const KNOWN_SUFFIXES = [...new Set(Object.values(CHANNEL_SUFFIX))].sort((a, b) => b.length - a.length);

/**
 * 去掉码上已经带着的渠道后缀。
 *
 * **为什么需要**:派生的来源码不一定是 campaign 主码。Maya 写 IG 稿时记录的是
 * **真正进文案的那个码**(即变体码),所以 `calendar_items.campaign` 里存的是
 * `CODEATTR10-IG` 而不是 `CODEATTR10`。再接一次就变成 `CODEATTR10-IG-IG` ——
 * 一个 Shopify 里不存在的码,于是挂了 campaign 的帖子核销数永远是 0。
 * (2026-08-26 在 dev 上一篇真实发布的帖子上实测到。)
 *
 * 先剥后接也顺带纠正了错配:这一行**就是** IG 那一行,它的码就该是 IG 变体。
 *
 * **代价,说清楚**:主码本身以渠道后缀结尾的商家(`SUMMER-X`)会丢掉那个尾巴,
 * 拿到 `SUMMER-IG` 而不是 `SUMMER-X-IG`。这是两害相权取轻:那种码罕见且是推测,
 * 而重复接后缀在 agent 的正常路径上是必然发生且静默的。
 */
function stripChannelSuffix(base: string): string {
  for (const suffix of KNOWN_SUFFIXES) {
    const tail = `-${suffix}`;
    if (base.endsWith(tail) && base.length > tail.length) return base.slice(0, -tail.length);
  }
  return base;
}

/** 这个渠道是否有变体码可言（IG 一定有——它靠这个活）。 */
export function channelHasVariant(channel: string | null | undefined): boolean {
  return Boolean(CHANNEL_SUFFIX[(channel ?? '').trim().toLowerCase()]);
}
